<template>
  <div class="legend">
    <CustomTabs
      :tab="activeTab"
      :data="tabsData"
      @change_tab="activeTab = $event"
      alt
      paddingless
    >
      <template v-slot:default="{ data: tabItem }">
        <div v-if="tabItem.name === 'requests'">
          <q-linear-progress v-if="isFetching" indeterminate />
          <NotFoundText v-if="!data" />
          <div v-else class="content">
            <CustomTable
              title="Заявки на материалы по подъезду"
              :rowsData="this.groupedRowsData"
              :columns="this.materialRequestColumns"
              :loading="isFetching"
              :show-search="true"
              :custom-body="true"
              :on-add-click="this.onAddClick"
              :sticky-header="false"
              :add-btn="false"
            >
              <template #top-right>
                <q-btn
                  size="sm"
                  color="teal"
                  icon="playlist_add"
                  label="Создать заявку на материалы"
                  dense
                  @click="onAddClickBulk"
                  title="Создать заявку на материалы"
                  class="bulk-add-btn"
                />
              </template>
              <template #customBody="props">
                <!-- Строка-заголовок группы (заявка + дата) -->
                <template v-if="props.row._isGroupHeader">
                  <q-tr class="group-header-row">
                    <custom-table-cell
                      :colspan="materialRequestColumns.length + 1"
                      align="left"
                      vertical-align="center"
                      class="group-header-cell"
                    >
                      <div class="group-header-content">
                        <div class="group-header-left">
                          <span class="group-request-label">
                            <q-icon
                              name="receipt_long"
                              size="18px"
                              color="primary"
                            />
                            Заявка #{{ props.row._groupRequestId }}
                          </span>
                          <span
                            class="group-date-label"
                            v-if="props.row._groupShippingDate"
                          >
                            <q-icon
                              name="local_shipping"
                              size="16px"
                              color="blue-7"
                            />
                            Отгрузка: {{ props.row._groupShippingDate }}
                          </span>
                          <span
                            class="group-provider-label"
                            v-if="props.row._groupProviderName"
                          >
                            <q-icon
                              name="business"
                              size="16px"
                              color="teal-7"
                            />
                            {{ props.row._groupProviderName }}
                          </span>
                          <span
                            class="group-agreement-label"
                            v-if="props.row._groupAgreementNum"
                          >
                            <q-icon
                              name="description"
                              size="16px"
                              color="deep-purple-7"
                            />
                            Договор №{{ props.row._groupAgreementNum }}
                            <span v-if="props.row._groupDocumentDate">
                              от {{ props.row._groupDocumentDate }}</span
                            >
                          </span>
                        </div>
                        <div class="group-header-right">
                          <div class="group-invoices-toggle">
                            <span
                              v-if="getInvoicesCountByRequestId(props.row._groupRequestId, props.row._groupInvoicesCnt) > 0"
                              class="group-invoices-count"
                            >
                              {{ getInvoicesCountByRequestId(props.row._groupRequestId, props.row._groupInvoicesCnt) }}
                              накладн.
                            </span>
                            <CustomLoader
                              v-if="loadingInvoices[props.row._groupRequestId]"
                              small
                            />
                            <CustomIcon
                              v-else
                              :icon="expandedRows[props.row._groupRequestId] ? 'expand_less' : 'expand_more'"
                              :primary="true"
                              @click="() => toggleInvoices(props.row._groupRequestId)"
                              style="cursor: pointer;"
                              title="Показать накладные"
                            />
                          </div>
                          <span class="group-count"
                            >{{ props.row._groupCount }} позиций</span
                          >
                          <span class="group-sum">
                            <Amount :value="props.row._groupSum" />
                          </span>
                        </div>
                      </div>
                    </custom-table-cell>
                  </q-tr>

                  <!-- Накладные для группы (один раз под шапкой) -->
                  <q-tr
                    v-if="expandedRows[props.row._groupRequestId]"
                    class="invoices-row"
                  >
                    <custom-table-cell
                      :colspan="materialRequestColumns.length + 1"
                      align="left"
                      vertical-align="top"
                      style="padding: 16px; background-color: #f5f5f5;"
                    >
                      <div
                        v-if="getInvoicesCountByRequestId(props.row._groupRequestId, props.row._groupInvoicesCnt) === 0"
                        class="no-invoices"
                      >
                        Нет накладных
                      </div>
                      <div v-else class="invoices-container">
                        <div
                          v-for="(invoice, index) in getInvoicesByRequestId(props.row._groupRequestId)"
                          :key="invoice.provider_request_invoice_id || index"
                          class="invoice-item"
                        >
                          <div class="invoice-header">
                            <span class="invoice-number">
                              <strong>Накладная:</strong>
                              {{ invoice.invoice_num || '-' }}
                            </span>
                            <div class="invoice-header-right">
                              <!-- Статус подписи накладной (универсальный способ через sign_info) -->
                              <div class="sign-status-box">
                                <template v-if="getSignInfo(invoice)">
                                  <span class="sign-label">Подписи:</span>
                                  <div
                                    v-for="(signature, idx) in getSignInfo(invoice).signatures"
                                    :key="idx"
                                    class="sign-status-item"
                                  >
                                    <q-icon
                                      :name="getSignIcon(signature.status).icon"
                                      :color="getSignIcon(signature.status).color"
                                      size="20px"
                                    />
                                    <q-tooltip>
                                      {{ signature.description || `Подпись ${idx + 1}`

                                      }}:
                                      {{ getSideSignLabel(signature.status) }}
                                    </q-tooltip>
                                  </div>
                                </template>
                                <template v-else>
                                  <span class="sign-not-signed"
                                    >Не подписано</span
                                  >
                                </template>
                              </div>
                              <span
                                class="invoice-status"
                                :class="get1CStatusClass(invoice.is_send_to_1c)"
                              >
                                {{ get1CStatusLabel(invoice.is_send_to_1c) }}
                              </span>
                              <CustomDropdown
                                :loading="this.invoiceActionsLoading[invoice.provider_request_invoice_id]"
                                :onClick="(action) => this.handleInvoiceAction(invoice, action.action, props.row._groupRowCtx)"
                                :options="this.getInvoiceActionsDropdown(invoice, props.row._groupRowCtx)"
                                title="Действия"
                              />
                            </div>
                          </div>
                          <div class="invoice-details">
                            <div class="invoice-detail-item">
                              <span class="label">Дата:</span>
                              <span
                                class="value"
                                >{{ invoice.invoice_date || '-' }}</span
                              >
                            </div>
                            <div class="invoice-detail-item">
                              <span class="label">Сумма накладной:</span>
                              <span class="value">
                                <Amount :value="invoice.invoice_sum" />
                              </span>
                            </div>
                            <div
                              class="invoice-detail-item"
                              v-if="invoice.kod_bk"
                            >
                              <span class="label">Код БК:</span>
                              <span class="value">{{ invoice.kod_bk }}</span>
                            </div>
                            <div
                              class="invoice-detail-item"
                              v-if="invoice.error"
                            >
                              <span class="label">Ошибка 1С:</span>
                              <span
                                class="value error-text"
                                :title="invoice.error"
                              >
                                {{ truncateError(invoice.error) }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </custom-table-cell>
                  </q-tr>
                </template>
                <q-tr v-else-if="!props.row.all">
                  <custom-table-cell align="left" vertical-align="center">
                    {{ props.row.material_name }}
                  </custom-table-cell>
                  <custom-table-cell align="left" vertical-align="center">
                    <div class="request-info-cell">
                      <div class="info-row">
                        <q-icon
                          name="shopping_cart"
                          size="14px"
                          color="primary"
                        />
                        <span
                          class="info-label"
                          :class="{ 'not-set': !props.row.provider_request_purchase_type_name }"
                        >
                          {{ props.row.provider_request_purchase_type_name || 'Не указан' }}
                        </span>
                        <q-tooltip>Тип закупки</q-tooltip>
                      </div>
                      <div class="info-row">
                        <q-icon name="person" size="14px" color="teal" />
                        <span
                          class="info-label"
                          :class="{ 'not-set': !props.row.provider_request_initiator_type_name }"
                        >
                          {{ props.row.provider_request_initiator_type_name || 'Не указан' }}
                        </span>
                        <q-tooltip>Инициатор</q-tooltip>
                      </div>
                      <div class="info-row">
                        <q-icon name="warehouse" size="14px" color="orange" />
                        <span
                          class="info-label"
                          :class="{ 'not-set': !props.row.naimenovanie }"
                        >
                          {{ props.row.naimenovanie || 'Не указан' }}
                        </span>
                        <q-tooltip>Склад</q-tooltip>
                      </div>
                    </div>
                  </custom-table-cell>
                  <custom-table-cell align="right" vertical-align="center">
                    <div
                      class="qty-cell"
                      :class="{ 'qty-cell--editable': canEditQty(props.row) }"
                      @click="() => (canEditQty(props.row) ? openEditQtyModal(props.row) : null)"
                    >
                      <span class="qty-value">
                        {{ props.row.material_cnt }}
                        {{ props.row.sell_unit_name }}
                        <q-tooltip>Количество из заявки</q-tooltip>
                      </span>
                      <CustomIcon
                        v-if="canEditQty(props.row)"
                        icon="edit"
                        :primary="true"
                        class="qty-edit-icon"
                        title="Изменить количество"
                      />
                    </div>
                  </custom-table-cell>
                  <custom-table-cell align="right" vertical-align="center">
                    <span class="qty-value">
                      {{ formatQty3(props.row.unshipped_cnt) }}
                      {{ props.row.sell_unit_name || '' }}
                    </span>
                  </custom-table-cell>
                  <custom-table-cell align="right" vertical-align="center">
                    <Amount :value="props.row.price" />
                  </custom-table-cell>
                  <custom-table-cell align="right" vertical-align="center">
                    <Amount :value="props.row.material_sum" />
                  </custom-table-cell>
                  <custom-table-cell align="center" vertical-align="center">
                    <div class="dates-cell">
                      <div class="date-row">
                        <q-icon
                          name="add_circle_outline"
                          size="14px"
                          color="green-7"
                        />
                        <span
                          class="date-value"
                          >{{ props.row.date_create || '—' }}</span
                        >
                        <q-tooltip>Дата заказа</q-tooltip>
                      </div>
                      <div class="date-separator"></div>
                      <div
                        class="date-row with-day"
                        v-if="props.row.date_shipping"
                      >
                        <div class="date-with-day">
                          <div class="date-line">
                            <q-icon
                              name="local_shipping"
                              size="14px"
                              color="blue-7"
                            />
                            <span
                              class="date-value"
                              >{{ props.row.date_shipping }}</span
                            >
                          </div>
                          <span
                            class="day-badge"
                            >{{ getDayOfWeek(props.row.date_shipping) }}</span
                          >
                        </div>
                        <q-tooltip>Дата отгрузки</q-tooltip>
                      </div>
                      <div class="date-row not-set" v-else>
                        <q-icon
                          name="local_shipping"
                          size="14px"
                          color="grey-5"
                        />
                        <span class="date-value">—</span>
                        <q-tooltip>Дата отгрузки не указана</q-tooltip>
                      </div>
                    </div>
                  </custom-table-cell>
                  <custom-table-cell align="left" vertical-align="center">
                    {{ props.row.provider_request_status_name }}
                  </custom-table-cell>
                  <custom-table-cell align="center" vertical-align="center">
                    <span class="invoice-brief">
                      {{ getInvoiceBrief(props.row) }}
                    </span>
                  </custom-table-cell>
                  <custom-table-cell align="center" vertical-align="center">
                    <div
                      style="display: flex; align-items: center; justify-content: center;"
                    >
                      <CustomIcon
                        v-if="!!props.row.expeditor_file_url && !this.downloading[props.row.provider_request_item_id]"
                        icon="cloud_download"
                        :primary="true"
                        @click="() => handleDownload(props.row.expeditor_file_url, props.row.provider_request_item_id)"
                      />
                      <CustomLoader
                        small
                        v-if="!!this.downloading[props.row.provider_request_item_id]"
                      />
                    </div>
                  </custom-table-cell>
                  <custom-table-cell align="center" vertical-align="center">
                    <CustomIcon
                      v-if="props.row.provider_request_status_code === 'CREATE' || props.row.provider_request_status_code === 'BRING_TO_CONTRACTOR'"
                      icon="trash"
                      :approve-mode="true"
                      :approve="() => this.deleteItem(props.row.provider_request_item_id)"
                      :loading="this.deleting[props.row.provider_request_item_id]"
                    />
                  </custom-table-cell>
                </q-tr>
                <q-tr v-if="props.row.all">
                  <custom-table-cell
                    colspan="3"
                    align="left"
                    vertical-align="center"
                  >
                    <b>Итого</b>
                  </custom-table-cell>
                  <custom-table-cell align="right" vertical-align="center">
                  </custom-table-cell>
                  <custom-table-cell align="right" vertical-align="center">
                    <b><Amount :value="props.row.amount" /></b>
                  </custom-table-cell>
                  <custom-table-cell
                    colspan="6"
                    align="left"
                    vertical-align="center"
                  >
                  </custom-table-cell>
                </q-tr>
              </template>
            </CustomTable>
          </div>
        </div>
        <div v-else-if="tabItem.name === 'overspend'">
          <MaterialsOverspend :params="params" />
        </div>
      </template>
    </CustomTabs>
  </div>
</template>

<script>
import Amount from "@/components/common/Amount.vue";
import CustomIcon from "@/components/common/CustomIcon.vue";
import CustomLoader from "@/components/common/CustomLoader";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomTabs from "@/components/common/CustomTabs";
import CustomDropdown from "@/components/common/CustomDropdown";
import MaterialsOverspend from "@/views/MaterialsOverspend/index.vue";
import { FILE_URL_OFFICE, MODAL_NAMES } from '@/constants';
import { downloadFile } from "@/services";
import { useAppStore } from "@/services/store/app";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { deleteEntranceMaterialRequest, getEntranceMaterialRequests, getProviderRequestInvoices, materialRequestColumns, sendInvoiceTo1C, signInvoice, downloadInvoice, downloadRealize } from './services';

export default {
  name: "MaterialRequests",
  components: {CustomTable, CustomTableCell, Amount, CustomIcon, CustomLoader, CustomDropdown, CustomTabs, MaterialsOverspend},
  props: ['params'],
  data() {
    return {
      materialRequestColumns,
      isFetching: false,
      data: null,
      deleting: {},
      downloading: {},
      expandedRows: {},
      invoiceActionsLoading: {},
      invoicesData: {},
      loadingInvoices: {},
      activeTab: 'requests',
      tabsData: [
        { name: 'requests', label: 'Заявки на материалы' },
        { name: 'overspend', label: 'Перерасход материалов' },
      ],
    }
  },
  methods: {
    getInvoiceBrief(row) {
      if (!row) return '—'
      const num = row.invoice_num
      const date = row.invoice_date
      if (num && date) return `№ ${num} от ${date}`
      if (num) return `№ ${num}`
      if (date) return `от ${date}`
      return '—'
    },
    formatQty3(value) {
      if (value == null || value === '') return '—'
      const num = Number(value)
      if (Number.isNaN(num)) return '—'
      return num.toLocaleString('ru-RU', {
        maximumFractionDigits: 3,
      })
    },
    isSameQty(row) {
      // Проверяем совпадают ли количество и единицы
      return row.qty_atom === row.material_cnt && row.atom_unit_name === row.sell_unit_name
    },
    canEditQty(row) {
      if (!row || row._isGroupHeader || row.all) return false
      return Number(row.invoices_cnt || 0) > 0
    },
    openEditQtyModal(row) {
      if (!this.canEditQty(row)) return
      this.showModal(MODAL_NAMES.EditMaterialRequestQty, {
        title: 'Изменить количество',
        data: { params: this.params, row },
        onSubmit: (res) => {
          this.data = res || []
        },
      })
    },
    getDayOfWeek(dateStr) {
      if (!dateStr) return ''
      // Парсим дату в формате DD.MM.YYYY или YYYY-MM-DD
      let date
      if (dateStr.includes('.')) {
        const [day, month, year] = dateStr.split('.')
        date = new Date(year, month - 1, day)
      } else {
        date = new Date(dateStr)
      }
      if (isNaN(date.getTime())) return ''
      const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
      return days[date.getDay()]
    },
    parseDateForSort(dateStr) {
      if (!dateStr || dateStr === 'Без даты') return 0
      // Парсим дату в формате DD.MM.YYYY или YYYY-MM-DD
      let date
      if (dateStr.includes('.')) {
        const [day, month, year] = dateStr.split('.')
        date = new Date(year, month - 1, day)
      } else {
        date = new Date(dateStr)
      }
      return isNaN(date.getTime()) ? 0 : date.getTime()
    },
    async getData() {
      this.isFetching = true
      const res = await getEntranceMaterialRequests(this.params)
      this.isFetching = false
      if(!res) return
      this.data = res
    },
    async reloadDataSilently() {
      try {
        const res = await getEntranceMaterialRequests(this.params)
        if(!res) return
        this.data = res
      } catch (e) {
        console.error('Ошибка тихой загрузки данных:', e)
      }
    },
    async reloadInvoices(provider_request_id) {
      try {
        const invoices = await getProviderRequestInvoices(provider_request_id)
        this.invoicesData = {
          ...this.invoicesData,
          [provider_request_id]: invoices
        }
      } catch (e) {
        console.error('Ошибка перезагрузки накладных:', e)
      }
    },
    async deleteItem(provider_request_item_id) {
      this.deleting[provider_request_item_id] = true
      const res = await deleteEntranceMaterialRequest(this.params, provider_request_item_id)
      this.deleting[provider_request_item_id] = false
      if(!res) return
      this.data = res
    },
    async handleDownload(fileUrl, provider_request_item_id) {
      if(!fileUrl || this.downloading[provider_request_item_id]) return
      const url = `${FILE_URL_OFFICE}${fileUrl}`
      const fileName = fileUrl.split('/').pop()
      this.downloading[provider_request_item_id] = true
      await downloadFile(url, fileName, {}, 'pdf', 'get')
      this.downloading[provider_request_item_id] = false
    },
    onAddClick() {
      this.showModal(MODAL_NAMES.AddMaterialRequest, {
        title: 'Добавить заявку на материалы',
        data: {params: this.params},
        onSubmit: (res) => {
          this.data = res || []
        },
      })
    },
    onAddClickBulk() {
      this.showModal(MODAL_NAMES.AddMaterialRequestBulk, {
        title: 'Создать заявку на материалы',
        data: {params: this.params},
        onSubmit: () => {
          this.getData()
        },
      })
    },
    getInvoices(row) {
      const invoices = this.invoicesData[row.provider_request_id]
      if (!invoices || !Array.isArray(invoices)) return []
      return invoices
    },
    getInvoicesCount(row) {
      // Если накладные уже загружены — показываем актуальное количество
      const loadedInvoices = this.invoicesData[row.provider_request_id]
      if (loadedInvoices && Array.isArray(loadedInvoices)) {
        return loadedInvoices.length
      }
      // Иначе используем счётчик из БД
      return row.invoices_cnt || 0
    },
    getInvoicesByRequestId(provider_request_id) {
      if (provider_request_id == null) return []
      const invoices = this.invoicesData[provider_request_id]
      if (!invoices || !Array.isArray(invoices)) return []
      return invoices
    },
    getInvoicesCountByRequestId(provider_request_id, fallbackCount = 0) {
      if (provider_request_id == null) return fallbackCount || 0
      const loadedInvoices = this.invoicesData[provider_request_id]
      if (loadedInvoices && Array.isArray(loadedInvoices)) {
        return loadedInvoices.length
      }
      return fallbackCount || 0
    },
    async toggleInvoices(provider_request_id) {
      const isExpanded = this.expandedRows[provider_request_id]

      // При раскрытии всегда загружаем накладные заново
      if (!isExpanded) {
        this.loadingInvoices[provider_request_id] = true
        try {
          const invoices = await getProviderRequestInvoices(provider_request_id)
          this.invoicesData = {
            ...this.invoicesData,
            [provider_request_id]: invoices
          }
        } catch (e) {
          console.error('Ошибка загрузки накладных:', e)
        } finally {
          this.loadingInvoices[provider_request_id] = false
        }
      }

      this.expandedRows = {
        ...this.expandedRows,
        [provider_request_id]: !isExpanded
      }
    },
    getInvoiceActionsDropdown(invoice, row) {
      const signInfo = this.getSignInfo(invoice)
      const hasSignGroup = !!invoice.sign_group_id

      // Проверяем статус подписи подрядчика
      const contractorSignature = signInfo?.signatures?.find(s => s.type_code === 'INVOICE_CONTRACTOR_SIGN')
      const contractorSigned = contractorSignature?.status === 'SIGNED'

      // Можно подписать если:
      // 1. Есть sign_group_id (поставщик уже загрузил накладную)
      // 2. Ещё не подписано застройщиком
      // 3. Следующий подписант — застройщик ИЛИ подпись в статусе WAITING (уже инициирована)
      const isNextContractor = signInfo?.next_to_sign?.type_code === 'INVOICE_CONTRACTOR_SIGN'
      const isContractorWaiting = contractorSignature?.status === 'WAITING'
      const canSign = hasSignGroup && !contractorSigned && (isNextContractor || isContractorWaiting)

      const isFullySigned = signInfo?.is_fully_signed === true

      // Проверяем статус отправки в 1С: 0 - не отправлен, 1 - готов к отправке, 2 - отправлен
      const isSentTo1C = invoice.is_send_to_1c === 2
      const canSendTo1C = isFullySigned && !isSentTo1C

      // Логика отображения кнопок 1С в зависимости от инициатора и типа поставщика
      const isProjectTeam = row?.provider_request_initiator_type_code === 'PROJECT_TEAM'
      const isTradingHouse = invoice.is_trading_house === true

      const actions = [
        {
          label: contractorSigned ? 'Подписано' : 'Подписать',
          action: 'sign_contractor',
          icon: 'verified',
          group: 'actions',
          disabled: !canSign || contractorSigned,
          hint: contractorSigned ? 'Уже подписано' : (!canSign ? 'Подписание недоступно' : '')
        },
        {
          label: 'Скачать накладную',
          action: 'download',
          icon: 'download',
          group: 'actions',
          disabled: !hasSignGroup,
          hint: !hasSignGroup ? 'Накладная ещё не подписана' : ''
        },
      ]

      // Добавляем кнопку 1С в зависимости от услови
      const isContractor = row?.provider_request_initiator_type_code === 'CONTRACTOR'

      if (isProjectTeam && isTradingHouse) {
        // Инициатор команда проекта + поставщик ТД: реализация в ТД + поступление в КП
        actions.push({
          label: 'Создать реализацию в 1С ТД и Поступление в 1С КП',
          action: 'send_to_1c_td_kp',
          icon: 'send',
          group: 'actions',
          disabled: !canSendTo1C,
          hint: isSentTo1C ? 'Уже отправлено в 1С' : (!isFullySigned ? 'Накладная должна быть полностью подписана' : '')
        })
      } else if (isProjectTeam && !isTradingHouse) {
        // Инициатор команда проекта + поставщик НЕ ТД: только поступление в КП
        actions.push({
          label: 'Создать поступление в 1С КП',
          action: 'send_to_1c_kp',
          icon: 'send',
          group: 'actions',
          disabled: !canSendTo1C,
          hint: isSentTo1C ? 'Уже отправлено в 1С' : (!isFullySigned ? 'Накладная должна быть полностью подписана' : '')
        })
      } else if (isContractor && isTradingHouse) {
        // Инициатор подрядчик + поставщик ТД: только реализация в ТД
        actions.push({
          label: 'Создать реализацию в 1С ТД',
          action: 'send_to_1c_td',
          icon: 'send',
          group: 'actions',
          disabled: !canSendTo1C,
          hint: isSentTo1C ? 'Уже отправлено в 1С' : (!isFullySigned ? 'Накладная должна быть полностью подписана' : '')
        })
      }
      // Если инициатор подрядчик и поставщик НЕ ТД - кнопка 1С не добавляется

      return actions
    },
    get1CStatusLabel(status) {
      // 0 - не отправлен, 1 - готов к отправке, 2 - отправлен
      if (status === 2) return 'Отправлено в 1С'
      if (status === 1) return 'Готов к отправке'
      return 'Не отправлено'
    },
    get1CStatusClass(status) {
      // 0 - не отправлен, 1 - готов к отправке, 2 - отправлен
      if (status === 2) return 'sent'
      if (status === 1) return 'ready'
      return 'not-sent'
    },
    getSideSignLabel(status) {
      if (status === 'SIGNED') return 'Подписан'
      if (status === 'WAITING') return 'На подписи'
      if (!status) return 'Не подписан'
      return status
    },
    getSignIcon(status) {
      if (status === 'SIGNED') {
        return { icon: 'check_circle', color: 'positive' }
      }
      if (status === 'WAITING') {
        return { icon: 'hourglass_empty', color: 'warning' }
      }
      return { icon: 'cancel', color: 'grey-5' }
    },
    getSignInfo(invoice) {
      // Получаем полную информацию о подписях из sign_info
      if (!invoice.sign_info) {
        return null
      }

      // Парсим если пришло как JSON строка
      if (typeof invoice.sign_info === 'string') {
        try {
          return JSON.parse(invoice.sign_info)
        } catch (e) {
          return null
        }
      }

      return invoice.sign_info
    },
    truncateError(error) {
      if (!error) return '-'
      const maxLength = 50
      if (error.length <= maxLength) return error
      return error.substring(0, maxLength) + '...'
    },
    async handleInvoiceAction(invoice, action, row) {
      const invoiceId = invoice.provider_request_invoice_id
      const provider_request_id = invoice.provider_request_id

      switch (action) {
        case 'sign_contractor':
          this.invoiceActionsLoading[invoiceId] = true
          try {
            const res = await signInvoice(invoiceId, 'contractor')
            if (res?.sign_url) {
              window.open(res.sign_url, '_blank')
            }
            await this.reloadInvoices(provider_request_id)
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.response?.data?.data?.message || error?.message || 'Ошибка при инициации подписания'
            this.showErrorSnackbar(errorMessage)
          } finally {
            this.invoiceActionsLoading[invoiceId] = false
          }
          break
        case 'download':
          this.invoiceActionsLoading[invoiceId] = true
          try {
            await downloadInvoice(invoiceId, invoice.invoice_num)
            this.showSucceedSnackbar('Накладная успешно скачана')
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.response?.data?.data?.message || error?.message || 'Ошибка при скачивании накладной'
            this.showErrorSnackbar(errorMessage)
          } finally {
            this.invoiceActionsLoading[invoiceId] = false
          }
          break
        case 'send_to_1c_td_kp':
          this.invoiceActionsLoading[invoiceId] = true
          try {
            const res = await sendInvoiceTo1C(invoiceId, 'td_kp')
            const message = res?.data?.message || res?.message || 'Реализация в 1С ТД и Поступление в 1С КП успешно созданы'
            this.showSucceedSnackbar(message)
            await this.reloadInvoices(provider_request_id)
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.response?.data?.data?.message || error?.message || 'Ошибка при создании документов в 1С'
            this.showErrorSnackbar(errorMessage)
          } finally {
            this.invoiceActionsLoading[invoiceId] = false
          }
          break
        case 'send_to_1c_kp':
          this.invoiceActionsLoading[invoiceId] = true
          try {
            const res = await sendInvoiceTo1C(invoiceId, 'kp')
            const message = res?.data?.message || res?.message || 'Поступление успешно создано в 1С КП'
            this.showSucceedSnackbar(message)
            await this.reloadInvoices(provider_request_id)
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.response?.data?.data?.message || error?.message || 'Ошибка при создании поступления в 1С КП'
            this.showErrorSnackbar(errorMessage)
          } finally {
            this.invoiceActionsLoading[invoiceId] = false
          }
          break
        case 'send_to_1c_td':
          this.invoiceActionsLoading[invoiceId] = true
          try {
            const res = await sendInvoiceTo1C(invoiceId, 'td')
            const message = res?.data?.message || res?.message || 'Реализация успешно создана в 1С ТД'
            this.showSucceedSnackbar(message)
            await this.reloadInvoices(provider_request_id)
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.response?.data?.data?.message || error?.message || 'Ошибка при создании реализации в 1С ТД'
            this.showErrorSnackbar(errorMessage)
          } finally {
            this.invoiceActionsLoading[invoiceId] = false
          }
          break
      }
    }
  },
  computed: {
    rowsData() {
      if(!this.data?.length) return []
      const amount = this.data?.reduce((prev, cur) => prev + cur.material_sum , 0)
      return [...this.data, {all: true, amount}]
    },
    // Группировка данных по номеру заявки и дате отгрузки
    groupedRowsData() {
      if (!this.data?.length) return []

      // Группируем по provider_request_id
      const groups = {}

      this.data.forEach(row => {
        const requestId = row.provider_request_id || 0
        const shippingDateKey = row.date_shipping || 'Без даты'
        const groupKey = `${requestId}_${shippingDateKey}`

        if (!groups[groupKey]) {
          groups[groupKey] = {
            requestId,
            shippingDate: shippingDateKey,
            providerName: row.provider_name || null,
            agreementNum: row.agreement_num || null,
            documentDate: row.document_date || null,
            invoicesCnt: row.invoices_cnt || 0,
            rowCtx: row,
            items: []
          }
        }
        groups[groupKey].invoicesCnt = Math.max(
          groups[groupKey].invoicesCnt || 0,
          row.invoices_cnt || 0
        )
        groups[groupKey].items.push(row)
      })

      // Сортируем группы по дате отгрузки (новые сверху) и номеру заявки
      const sortedKeys = Object.keys(groups).sort((a, b) => {
        const groupA = groups[a]
        const groupB = groups[b]

        // Сначала сортируем по дате отгрузки (новые сверху)
        const dateA = this.parseDateForSort(groupA.shippingDate)
        const dateB = this.parseDateForSort(groupB.shippingDate)
        if (dateA !== dateB) {
          return dateB - dateA // Новые сверху
        }

        // Затем по номеру заявки (больший номер сверху)
        return groupB.requestId - groupA.requestId
      })

      // Формируем плоский массив со строками-разделителями
      const result = []
      sortedKeys.forEach(key => {
        const group = groups[key]

        // Добавляем строку-разделитель (заголовок группы)
        result.push({
          _isGroupHeader: true,
          _groupRequestId: group.requestId,
          _groupShippingDate: group.shippingDate !== 'Без даты' ? group.shippingDate : null,
          _groupProviderName: group.providerName,
          _groupAgreementNum: group.agreementNum,
          _groupDocumentDate: group.documentDate,
          _groupCount: group.items.length,
          _groupSum: group.items.reduce((sum, item) => sum + (item.material_sum || 0), 0),
          _groupInvoicesCnt: group.invoicesCnt || 0,
          _groupRowCtx: group.rowCtx || null
        })

        // Добавляем позиции этой группы
        group.items.forEach(item => {
          result.push(item)
        })
      })

      // Добавляем итоговую строку
      const totalAmount = this.data.reduce((prev, cur) => prev + cur.material_sum, 0)
      result.push({ all: true, amount: totalAmount })

      return result
    },
  },
  watch: {
    params: {
      deep: true,
      immediate: false,
      handler(newParams) {
        if (newParams?.resident_id != null && newParams?.project_type_id != null) {
          this.getData()
        }
      },
    },
  },
  mounted() {
    this.getData()
  },
  setup() {
    const {showModal} = useAppStore()
    const {showSucceedSnackbar, showErrorSnackbar} = useCustomSnackbar()
    return {showModal, showSucceedSnackbar, showErrorSnackbar}
  }
};
</script>

<style scoped lang="scss">
.content {
  padding-top: 10px;
}

.bulk-add-btn {
  margin-right: 8px;
  // display: none;
}

// Стили для группировки по заявкам
.group-header-row {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%) !important;
  border-top: 2px solid #1976d2;
}

.group-header-cell {
  padding: 12px 16px !important;
}

.group-invoices-count {
  font-size: 12px;
  color: #1976d2;
}

.group-invoices-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.invoice-brief {
  font-size: 12px;
  color: #1976d2;
  white-space: nowrap;
}

.group-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.group-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.group-request-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 600;
  color: #1565c0;
}

.group-date-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #616161;
  background: rgba(255, 255, 255, 0.7);
  padding: 4px 10px;
  border-radius: 4px;
}

.group-provider-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #00695c;
  background: rgba(255, 255, 255, 0.7);
  padding: 4px 10px;
  border-radius: 4px;
  font-weight: 500;
}

.group-agreement-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #5e35b1;
  background: rgba(255, 255, 255, 0.7);
  padding: 4px 10px;
  border-radius: 4px;
}

.group-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.group-count {
  font-size: 13px;
  color: #757575;
  background: rgba(255, 255, 255, 0.7);
  padding: 4px 10px;
  border-radius: 4px;
}

.group-sum {
  font-size: 14px;
  font-weight: 600;
  color: #2e7d32;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 12px;
  border-radius: 4px;
}

.request-info-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .info-row {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: default;

    .info-label {
      font-size: 12px;
      color: #444;
      line-height: 1.2;

      &.not-set {
        color: #aaa;
        font-style: italic;
      }
    }
  }
}

.qty-cell {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  .qty-value {
    font-size: 13px;
    color: #333;
    cursor: default;
  }

  .qty-combined {
    display: flex;
    align-items: center;
    gap: 4px;

    .qty-part {
      font-size: 13px;
      color: #333;
      cursor: default;
    }

    .qty-separator {
      color: #999;
      font-size: 12px;
    }
  }
}

.qty-cell--editable {
  cursor: pointer;

  .qty-value,
  .qty-part {
    cursor: pointer;
  }
}

.qty-edit-icon {
  opacity: 0.75;
}

.qty-cell--editable:hover .qty-edit-icon {
  opacity: 1;
}

.dates-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .date-separator {
    height: 1px;
    background: #e0e0e0;
    margin: 2px 0;
  }

  .date-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    cursor: default;

    .date-value {
      font-size: 12px;
      color: #333;
    }

    .date-with-day {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1px;

      .date-line {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .day-badge {
        font-size: 10px;
        color: #1976d2;
        background: #e3f2fd;
        padding: 1px 5px;
        border-radius: 3px;
        font-weight: 600;
      }
    }

    &.not-set {
      .date-value {
        color: #aaa;
      }
    }
  }
}

.invoices-row {
  background-color: #f5f5f5;
}

.sign-status-box {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fafafa;
  margin-right: 8px;
}

.sign-label {
  font-size: 11px;
  color: #666;
  margin-right: 2px;
}

.sign-status-item {
  display: flex;
  align-items: center;
}

.sign-not-signed {
  font-size: 12px;
  color: #999;
  font-style: italic;
}

.no-invoices {
  text-align: center;
  color: #9e9e9e;
  padding: 20px;
  font-style: italic;
}

.invoices-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.invoice-item {
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
}

.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.invoice-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.invoice-number {
  font-size: 14px;
  color: #333;
}

.invoice-status {
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;

  &.sent {
    background-color: #e8f5e9;
    color: #2e7d32;
  }

  &.ready {
    background-color: #e3f2fd;
    color: #1565c0;
  }

  &.not-sent {
    background-color: #fff3e0;
    color: #e65100;
  }
}

.invoice-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
}

.invoice-detail-item {
  display: flex;
  gap: 8px;
  font-size: 13px;

  .label {
    color: #666;
    font-weight: 500;
  }

  .value {
    color: #333;

    &.error-text {
      color: #d32f2f;
      cursor: help;
    }
  }
}
</style>
